import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  Collapse,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ListAltIcon from '@mui/icons-material/ListAlt';
import RefreshIcon from '@mui/icons-material/Refresh';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import moment from 'moment-timezone';
import { OptionsHeaderBar } from '../../components/options/OptionsHeaderBar';
import { optionsService, EngineLogCategory, EngineLogRow, EngineLogsResponse } from '../../services/optionsService';

const CATEGORY_META: Record<EngineLogCategory, { label: string; color: string; bg: string }> = {
  OPENED: { label: 'Opened', color: '#15803D', bg: '#DCFCE7' },
  REJECTED: { label: 'Rejected', color: '#B91C1C', bg: '#FEE2E2' },
  NO_SIGNAL: { label: 'No signal', color: '#475569', bg: '#F1F5F9' },
  CAP_REACHED: { label: 'Cap / duplicate', color: '#B45309', bg: '#FEF3C7' },
  EXIT: { label: 'Exit', color: '#7C3AED', bg: '#EDE9FE' },
  SQUARE_OFF: { label: 'Square-off', color: '#0369A1', bg: '#E0F2FE' },
  LIFECYCLE: { label: 'Lifecycle', color: '#334155', bg: '#E2E8F0' },
  ERROR: { label: 'Error', color: '#FFFFFF', bg: '#DC2626' },
};

const CATEGORY_ORDER: EngineLogCategory[] = [
  'REJECTED',
  'OPENED',
  'EXIT',
  'CAP_REACHED',
  'NO_SIGNAL',
  'SQUARE_OFF',
  'ERROR',
  'LIFECYCLE',
];

const todayIst = () => moment().tz('Asia/Kolkata').format('YYYY-MM-DD');

const CategoryChip: React.FC<{ category: EngineLogCategory }> = ({ category }) => {
  const meta = CATEGORY_META[category];
  return (
    <Chip
      size="small"
      label={meta.label}
      sx={{ bgcolor: meta.bg, color: meta.color, fontWeight: 700, fontSize: 11, height: 22 }}
    />
  );
};

const LogRow: React.FC<{ row: EngineLogRow; showAccount: boolean }> = ({ row, showAccount }) => {
  const [open, setOpen] = useState(false);
  const hasDetail = row.data !== undefined && row.data !== null;
  const colSpan = showAccount ? 8 : 7;

  return (
    <>
      <TableRow hover sx={{ '& td': { borderBottom: open ? 'none' : undefined } }}>
        <TableCell padding="checkbox">
          {hasDetail && (
            <IconButton size="small" onClick={() => setOpen(!open)} aria-label="Show details">
              {open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
            </IconButton>
          )}
        </TableCell>
        <TableCell sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap', color: '#334155' }}>
          {moment(row.timestamp).tz('Asia/Kolkata').format('HH:mm:ss')}
        </TableCell>
        <TableCell>
          <CategoryChip category={row.category} />
        </TableCell>
        {showAccount && (
          <TableCell sx={{ whiteSpace: 'nowrap', color: '#334155', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {row.accountName ?? '—'}
          </TableCell>
        )}
        <TableCell sx={{ whiteSpace: 'nowrap', fontSize: 12, color: '#475569' }}>{row.strategy ?? '—'}</TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap', fontSize: 12, color: '#475569' }}>{row.scenario ?? row.state}</TableCell>
        <TableCell sx={{ fontSize: 12.5, color: '#1E293B', wordBreak: 'break-word' }}>{row.message}</TableCell>
        <TableCell
          align="right"
          sx={{
            fontFamily: 'monospace',
            whiteSpace: 'nowrap',
            fontWeight: 700,
            color: row.pnl === null ? '#94A3B8' : row.pnl >= 0 ? '#15803D' : '#B91C1C',
          }}
        >
          {row.pnl === null ? '—' : `${row.pnl >= 0 ? '+' : ''}₹${row.pnl.toLocaleString('en-IN')}`}
        </TableCell>
      </TableRow>
      {hasDetail && (
        <TableRow>
          <TableCell colSpan={colSpan} sx={{ py: 0, bgcolor: '#F8FAFC' }}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box
                component="pre"
                sx={{ m: 0, py: 1.5, px: 1, fontSize: 11.5, fontFamily: 'monospace', color: '#334155', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
              >
                {JSON.stringify(row.data, null, 2)}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

const EngineLogsPage: React.FC = () => {
  const [date, setDate] = useState<string>(todayIst());
  const [data, setData] = useState<EngineLogsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [category, setCategory] = useState<EngineLogCategory | 'ALL'>('ALL');
  const [account, setAccount] = useState<string>('ALL');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await optionsService.getEngineLogs(date);
      setData(res);
      setError(null);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load engine logs');
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => {
    load();
    // The engine writes a new outcome every 30s during market hours; only today's log changes.
    if (date !== todayIst()) return;
    const timer = setInterval(load, 30000);
    return () => clearInterval(timer);
  }, [load, date]);

  useEffect(() => setPage(0), [date, category, account, search]);

  const rows = data?.rows ?? [];

  const counts = useMemo(() => {
    const c: Partial<Record<EngineLogCategory, number>> = {};
    rows.forEach((r) => (c[r.category] = (c[r.category] || 0) + 1));
    return c;
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter(
      (r) =>
        (category === 'ALL' || r.category === category) &&
        (account === 'ALL' || r.accountId === account) &&
        (!q || `${r.message} ${r.strategy ?? ''} ${r.accountName ?? ''}`.toLowerCase().includes(q))
    );
  }, [rows, category, account, search]);

  const showAccount = (data?.accounts.length ?? 0) > 1;
  const pageRows = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ p: 3, bgcolor: '#F8FAFC', minHeight: '100vh' }}>
      <OptionsHeaderBar />

      <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid #E2E8F0', bgcolor: '#FFFFFF' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} spacing={1.5} sx={{ mb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <ListAltIcon sx={{ color: '#0284C7' }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1E293B' }}>
              ENGINE LOGS
            </Typography>
            {data?.session && (
              <Chip
                size="small"
                label={`${data.session.currentState} · ${data.session.tradesCount ?? 0} trades`}
                sx={{ fontWeight: 600, bgcolor: '#E0F2FE', color: '#0369A1' }}
              />
            )}
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              type="date"
              size="small"
              value={date}
              onChange={(e) => e.target.value && setDate(e.target.value)}
              inputProps={{ max: todayIst() }}
            />
            <Tooltip title="Refresh">
              <span>
                <IconButton onClick={load} disabled={loading} aria-label="Refresh logs">
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>
        <Typography variant="caption" sx={{ color: '#64748B', display: 'block', mb: 2 }}>
          Every evaluation outcome, rejection reason, exit and lifecycle step the options engine recorded for the day.
          Outcomes are logged when they change, so a repeated rejection appears once per change, not every 30 seconds.
          {date === todayIst() && ' Refreshes every 30 seconds.'}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
          <Chip
            label={`All · ${rows.length}`}
            onClick={() => setCategory('ALL')}
            variant={category === 'ALL' ? 'filled' : 'outlined'}
            sx={{ fontWeight: 700, ...(category === 'ALL' ? { bgcolor: '#1E293B', color: '#FFFFFF' } : {}) }}
          />
          {CATEGORY_ORDER.filter((c) => counts[c]).map((c) => (
            <Chip
              key={c}
              label={`${CATEGORY_META[c].label} · ${counts[c]}`}
              onClick={() => setCategory(c)}
              variant={category === c ? 'filled' : 'outlined'}
              sx={{
                fontWeight: 700,
                borderColor: CATEGORY_META[c].bg,
                ...(category === c ? { bgcolor: CATEGORY_META[c].bg, color: CATEGORY_META[c].color } : { color: CATEGORY_META[c].color }),
              }}
            />
          ))}
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
          <TextField
            size="small"
            placeholder="Search message, strategy or account"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flex: 1, maxWidth: { sm: 420 } }}
          />
          {showAccount && (
            <TextField select size="small" label="Account" value={account} onChange={(e) => setAccount(e.target.value)} sx={{ minWidth: 200 }}>
              <MenuItem value="ALL">All accounts</MenuItem>
              {data!.accounts.map((a) => (
                <MenuItem key={a.id} value={a.id}>
                  {a.name}
                </MenuItem>
              ))}
            </TextField>
          )}
        </Stack>

        {!error && data && !data.session && (
          <Alert severity="info">No options session was recorded for {date}. The market may have been closed.</Alert>
        )}

        {data?.session && (
          <>
            <TableContainer sx={{ border: '1px solid #E2E8F0', borderRadius: 1.5 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ '& th': { bgcolor: '#F1F5F9', fontWeight: 700, color: '#475569', fontSize: 12 } }}>
                    <TableCell padding="checkbox" />
                    <TableCell>Time (IST)</TableCell>
                    <TableCell>Type</TableCell>
                    {showAccount && <TableCell>Account</TableCell>}
                    <TableCell>Strategy</TableCell>
                    <TableCell>Scenario / State</TableCell>
                    <TableCell>Details</TableCell>
                    <TableCell align="right">P&amp;L</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pageRows.map((row) => (
                    <LogRow key={row.id} row={row} showAccount={showAccount} />
                  ))}
                  {pageRows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={showAccount ? 8 : 7} align="center" sx={{ py: 4, color: '#64748B' }}>
                        {loading ? 'Loading…' : 'No log entries match these filters.'}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filtered.length}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[25, 50, 100]}
            />
          </>
        )}
      </Paper>
    </Box>
  );
};

export default EngineLogsPage;
